import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  ShareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toBlob } from "html-to-image";

import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import WeaveLoader from "../components/common/WeaveLoader";
import SiteCard from "../components/portfolio/SiteCard";
import SummaryStrip from "../components/portfolio/SummaryStrip";
import Toolbar, { RANGES, VIEWS } from "../components/portfolio/Toolbar";
import { changePercent } from "../components/portfolio/format";
import { defaultMetric, resolveSiteMetric } from "../components/portfolio/metrics";
import { useToast } from "../contexts/ToastContext";
import { portfolioService } from "../services/portfolio";
import { getApiErrorMessage } from "../utils/axios";

const PRIVACY_KEY = "portfolio_privacy";
const VIEW_IDS = new Set(VIEWS.map((item) => item.id));

const Portfolio = ({ currentUser }) => {
  const { view: viewParam } = useParams();
  const view = VIEW_IDS.has(viewParam) ? viewParam : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDays = Number(searchParams.get("days")) || 28;
  const days = RANGES.some((range) => range.days === requestedDays) ? requestedDays : 28;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState(() => defaultMetric(viewParam));
  const [sort, setSort] = useState("value");
  const [layout, setLayout] = useState("grid");
  const [privacy, setPrivacy] = useState(() => {
    try {
      return localStorage.getItem(PRIVACY_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [sharing, setSharing] = useState(false);
  const [sharePreview, setSharePreview] = useState(null);
  const [shareHideAggregate, setShareHideAggregate] = useState(false);
  const [shareTopPerformers, setShareTopPerformers] = useState(false);
  const [pendingRecapture, setPendingRecapture] = useState(false);
  const shareRef = useRef(null);
  const { success: toastSuccess, error: toastError } = useToast();

  const showRange = view === "traffic" || view === "organic" || view === "speed" || view === "growth";
  const cloudflareView = view === "traffic" || view === "organic" || view === "speed";
  // Offscreen capture tree only — live page stays untouched.
  const showShareSurface = !!sharePreview || pendingRecapture || sharing;

  const load = useCallback(() => {
    if (!view) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    portfolioService.getPortfolio(
      { view, days: showRange ? days : 28 },
      (response) => {
        if (cancelled) return;
        setData(response.data);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.error
          || getApiErrorMessage(err, "Could not load portfolio analytics."),
        );
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [view, days, showRange]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    setMetric(defaultMetric(view));
  }, [view]);

  useEffect(() => () => {
    if (sharePreview?.url) URL.revokeObjectURL(sharePreview.url);
  }, [sharePreview]);

  const setPrivacyMode = (next) => {
    setPrivacy(next);
    try {
      localStorage.setItem(PRIVACY_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  const setDays = (nextDays) => {
    const params = new URLSearchParams(searchParams);
    params.set("days", String(nextDays));
    setSearchParams(params, { replace: true });
  };

  const sites = useMemo(() => {
    const list = [...(data?.sites || [])];
    if (sort === "name") {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sort === "change") {
      list.sort((a, b) => {
        const ra = resolveSiteMetric(a, view, metric);
        const rb = resolveSiteMetric(b, view, metric);
        const ca = changePercent(ra.value, ra.previous) ?? -Infinity;
        const cb = changePercent(rb.value, rb.previous) ?? -Infinity;
        return cb - ca;
      });
    } else {
      list.sort((a, b) => {
        const ra = resolveSiteMetric(a, view, metric);
        const rb = resolveSiteMetric(b, view, metric);
        return (rb.value || 0) - (ra.value || 0);
      });
    }
    return list;
  }, [data, sort, view, metric]);

  const shareSites = shareTopPerformers ? sites.slice(0, 9) : sites;

  const closeSharePreview = () => {
    setSharePreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setShareHideAggregate(false);
    setShareTopPerformers(false);
    setPendingRecapture(false);
    setSharing(false);
  };

  const handleShare = () => {
    if (sharing || pendingRecapture || loading || error) return;
    setPendingRecapture(true);
  };

  const handleShareOption = (key, checked) => {
    if (sharing || pendingRecapture) return;
    if (key === "hideAggregate") setShareHideAggregate(checked);
    else setShareTopPerformers(checked);
    setPendingRecapture(true);
  };

  useEffect(() => {
    if (!pendingRecapture) return undefined;
    let cancelled = false;
    (async () => {
      setSharing(true);
      // Wait for offscreen surface (and option changes) to paint.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (cancelled) return;
      if (!shareRef.current) {
        setSharing(false);
        setPendingRecapture(false);
        toastError("Could not capture screenshot");
        return;
      }
      try {
        // skipFonts: Google Fonts CSS is cross-origin; reading cssRules throws in prod.
        // style left/top: surface lives at -10000px; clone would otherwise paint off-canvas (blank PNG).
        const blob = await toBlob(shareRef.current, {
          pixelRatio: 2,
          backgroundColor: getComputedStyle(document.documentElement)
            .getPropertyValue("--theme-main-bg")
            .trim() || "#ffffff",
          skipFonts: true,
          style: { left: "0", top: "0", right: "auto", bottom: "auto" },
        });
        if (cancelled) return;
        if (!blob) throw new Error("Capture failed");
        const url = URL.createObjectURL(blob);
        setSharePreview((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url);
          return { blob, url };
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) toastError("Could not capture screenshot");
      } finally {
        if (!cancelled) {
          setSharing(false);
          setPendingRecapture(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingRecapture, shareHideAggregate, shareTopPerformers, toastError]);

  const handleCopyImage = async () => {
    if (!sharePreview?.blob) return;
    if (!navigator.clipboard?.write || !window.ClipboardItem) {
      toastError("Clipboard image copy is not supported in this browser");
      return;
    }
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": sharePreview.blob }),
      ]);
      toastSuccess("Copied image to clipboard");
    } catch (err) {
      console.error(err);
      toastError("Could not copy image");
    }
  };

  const handleDownloadImage = () => {
    if (!sharePreview?.url) return;
    const anchor = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    anchor.href = sharePreview.url;
    anchor.download = `analytics-${stamp}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    toastSuccess("Image downloaded");
  };

  if (!currentUser?.features?.portfolio) {
    return <Navigate to="/" replace />;
  }

  if (!view) {
    return <Navigate to="/analytics/traffic/" replace />;
  }

  const secondaryActions = [
    <button
      key="privacy"
      type="button"
      onClick={() => setPrivacyMode(!privacy)}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
        privacy
          ? "border-theme-text bg-theme-text text-theme-main"
          : "border-theme-secondary text-theme-text hover:opacity-80"
      }`}
      aria-pressed={privacy}
    >
      {privacy ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      Privacy
    </button>,
  ];

  const primaryAction = (
    <button
      type="button"
      onClick={handleShare}
      disabled={sharing || loading || !!error}
      className="inline-flex items-center gap-2 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
      style={{ backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }}
    >
      <ShareIcon className="h-4 w-4" />
      {sharing ? "Capturing…" : "Share"}
    </button>
  );

  return (
    <DashboardLayout
      currentUser={currentUser}
      title="Analytics"
      secondaryActions={secondaryActions}
      primaryAction={primaryAction}
    >
      <div className="flex w-full flex-col gap-5 pb-10">
        <Toolbar
          view={view}
          days={days}
          onDaysChange={setDays}
          metric={metric}
          onMetricChange={setMetric}
          sort={sort}
          onSortChange={setSort}
          layout={layout}
          onLayoutChange={setLayout}
          showRange={showRange}
        />

        {loading && (
          <div className="flex justify-center py-20">
            <WeaveLoader size={64} />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-theme-secondary bg-theme-main p-6 text-sm text-theme-text">
            {error}
            <button
              type="button"
              onClick={load}
              className="ml-3 underline opacity-70 hover:opacity-100"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && data && cloudflareView && data.configured === false && (
          <div className="rounded-xl border border-theme-secondary bg-theme-main p-6 text-sm text-theme-text">
            Analytics are temporarily unavailable. Traffic data could not be loaded.
            <button
              type="button"
              onClick={load}
              className="ml-3 underline opacity-70 hover:opacity-100"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && data && !(cloudflareView && data.configured === false) && (
          <>
            <div className="flex flex-col gap-5 rounded-2xl bg-theme-main p-1">
              {view === "indexing" && (
                <div className="rounded-xl border border-dashed border-theme-secondary px-4 py-3 text-sm text-theme-text opacity-70">
                  Indexing across all sites lands with Google Search Console. Cards below are ready for when it does.
                </div>
              )}

              <SummaryStrip
                view={view}
                totals={data.totals}
                siteCount={sites.length}
                days={days}
              />

              {sites.length === 0 ? (
                <div className="rounded-xl border border-theme-secondary px-6 py-16 text-center">
                  <div className="text-base font-medium text-theme-text">No sites yet</div>
                  <p className="mt-1 text-sm text-theme-text opacity-60">
                    Build a site and it will show up here with traffic, speed, and audit trends.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                    {sites.length} {sites.length === 1 ? "site" : "sites"}
                  </div>
                  {sites.length === 1 ? (
                    <SiteCard
                      site={sites[0]}
                      view={view}
                      metric={metric}
                      privacy={privacy}
                      hero
                      layout={layout}
                    />
                  ) : (
                    <div
                      className={
                        layout === "list"
                          ? "flex flex-col gap-2"
                          : "grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3"
                      }
                    >
                      {sites.map((site) => (
                        <SiteCard
                          key={site.hash}
                          site={site}
                          view={view}
                          metric={metric}
                          privacy={privacy}
                          layout={layout}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {showShareSurface && (
              <div
                ref={shareRef}
                aria-hidden
                className="pointer-events-none fixed left-[-10000px] top-0 w-[1120px] bg-theme-main p-1"
              >
                <div className="flex flex-col gap-5 rounded-2xl bg-theme-main p-1">
                  {!shareHideAggregate && (
                    <SummaryStrip
                      view={view}
                      totals={data.totals}
                      siteCount={sites.length}
                      days={days}
                    />
                  )}

                  {shareSites.length > 0 && (
                    <div>
                      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                        {shareTopPerformers
                          ? `Top ${shareSites.length} ${shareSites.length === 1 ? "site" : "sites"}`
                          : `${shareSites.length} ${shareSites.length === 1 ? "site" : "sites"}`}
                      </div>
                      <div
                        className={
                          layout === "list"
                            ? "flex flex-col gap-2"
                            : "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
                        }
                      >
                        {shareSites.map((site) => (
                          <SiteCard
                            key={`share-${site.hash}`}
                            site={site}
                            view={view}
                            metric={metric}
                            privacy={privacy}
                            layout={layout}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between px-2 pb-2 pt-1 text-[11px] text-theme-text opacity-50">
                    <span>
                      {VIEWS.find((item) => item.id === view)?.label}
                      {showRange ? ` · ${days} days` : ""}
                      {privacy ? " · Privacy on" : ""}
                    </span>
                    <span className="font-medium">Weave.md</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {sharePreview && (
        <Modal
          onClose={closeSharePreview}
          maxWidth="max-w-4xl"
          heightClass="max-h-[90vh]"
          noPadding
        >
          <div className="flex items-center justify-between border-b border-theme-secondary px-5 py-3">
            <div>
              <div className="text-sm font-semibold text-theme-text">Share preview</div>
              <div className="text-xs text-theme-text opacity-55">
                Download or copy the image
              </div>
            </div>
            <button
              type="button"
              onClick={closeSharePreview}
              className="rounded-lg p-1.5 text-theme-text opacity-60 hover:opacity-100 hover:bg-black hover:bg-opacity-5"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="relative min-h-0 flex-1 overflow-auto bg-black bg-opacity-5 p-4">
            <img
              src={sharePreview.url}
              alt="Analytics share preview"
              className={`mx-auto max-h-[60vh] w-auto max-w-full rounded-lg border border-theme-secondary shadow-sm transition-opacity ${
                sharing ? "opacity-50" : "opacity-100"
              }`}
            />
            {sharing && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <WeaveLoader size={40} />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-theme-secondary px-5 py-3">
            <div className="flex flex-wrap items-center gap-4 text-sm text-theme-text">
              <label className={`inline-flex items-center gap-2 ${sharing || pendingRecapture ? "opacity-50" : ""}`}>
                <input
                  type="checkbox"
                  checked={shareHideAggregate}
                  disabled={sharing || pendingRecapture}
                  onChange={(event) => handleShareOption("hideAggregate", event.target.checked)}
                  className="rounded border-theme-secondary"
                />
                Hide aggregate
              </label>
              <label className={`inline-flex items-center gap-2 ${sharing || pendingRecapture ? "opacity-50" : ""}`}>
                <input
                  type="checkbox"
                  checked={shareTopPerformers}
                  disabled={sharing || pendingRecapture}
                  onChange={(event) => handleShareOption("topPerformers", event.target.checked)}
                  className="rounded border-theme-secondary"
                />
                Top performers
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleCopyImage}
                disabled={sharing || pendingRecapture}
                className="inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-2 text-sm font-medium text-theme-text transition-colors hover:bg-black hover:bg-opacity-5 disabled:opacity-50"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Copy to clipboard
              </button>
              <button
                type="button"
                onClick={handleDownloadImage}
                disabled={sharing || pendingRecapture}
                className="inline-flex items-center gap-1.5 rounded-lg bg-theme-text px-3 py-2 text-sm font-medium text-theme-main transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download image
              </button>
            </div>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
};

export default Portfolio;
